/* eslint-disable no-param-reassign */
import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
  startTransition,
  useDeferredValue
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { differenceInMonths } from 'date-fns';
import NoSSR from 'react-no-ssr';
import {
  clientErrorTracker,
  trans,
  useSoyuzClientConfig,
  useSoyuzLocales,
  useSoyuzFlag,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';
import Metrics from '@spotahome/soyuz-tracking';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import {
  FabButton,
  FooterSimplified,
  SEOSnippet,
  SeoSnippetShape,
  FilterUtils,
  SearchBreadcrumbs,
  MapListSwitcher,
  useFiltersContext,
  Button,
  cityToggleConfig,
  CHANNELS
} from '@spotahome/ui-library';
import { InViewEvent } from '@spotahome/ui-library/src/tenant/components/withInViewEvent';
import { CustomDimensionsManager } from '@spotahome/ecommerce-tracking';

import internalLinkingModule from '@spotahome/marketplace-common/src/shapes/internalLinking';
import mapPointShape from '@spotahome/marketplace-common/src/shapes/mapPoint';

import Bell from '@spotahome/marketplace-common/src/components/icons/Bell';
import InternalLinkingModules from '@spotahome/marketplace-common/src/components/internalLinking/InternalLinkingModules';
import HeaderContainer from '@spotahome/marketplace-common/src/components/HeaderContainer';
import HomecardMap from '@spotahome/marketplace-common/src/components/HomecardMap';
import RefreshIcon from '@spotahome/marketplace-common/src/components/icons/Refresh';
import AlertButton from '@spotahome/marketplace-common/src/components/AlertButton';
import AppliedFiltersModal from '@spotahome/marketplace-common/src/components/AppliedFiltersModal';
import AlertInPastModal from '@spotahome/marketplace-common/src/components/AlertInPastModal';
import SearchTitle from '@spotahome/marketplace-common/src/components/SearchTitle';
import Paginator from '@spotahome/marketplace-common/src/components/Paginator';

import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';
import { removeSeoFilters } from '@spotahome/marketplace-common/src/utils/seo-mode';

import { getMarkersBoundsQuery } from '@spotahome/marketplace-common/src/graphql/queries/markers';
import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';

import SimplifiedCardPaginator from '@spotahome/marketplace-common/src/lib/simplified-card-paginator';
import { useHighlightMarkersContext } from '@spotahome/marketplace-common/src/context/HighlightMarkersContext';

import { useMediaQuery } from '@spotahome/ui-library/src/tenant/components/utils/breakpoints/breakpoints';

import SEOContent from '../SEOContent';
import SearchBanner from '../SearchBanner';
import FiltersHeader from '../FiltersHeader';
import FiltersExposed from '../FiltersExposed';

import HomeCardList from '../HomeCardList';
import MapPlaceholder from '../MapPlaceholder';
import ValueProposition from '../ValueProposition';
import RemoveFilters from '../RemoveFilters/RemoveFilters';
import ChartsSection from '../Charts/ChartsSection';
import { shouldShowCharts } from '../../utils/seoCharts';

const Map = lazy(() =>
  import('@spotahome/marketplace-common/src/components/Map')
);
const PlacesAutocomplete = lazy(() =>
  import('@spotahome/marketplace-common/src/components/PlacesAutocomplete')
);
const Filters = lazy(() => import('../Filters'));

// If a POI is defined, the list of markers will be retrieved ordered by distance
// and the frontend will order by relevance each set defined by this const.
const MAP_ID = 'themap';
const MAP_ID_VARIANT = 'themap--variant';
const PLACE_SELECTED_EVENT_CATEGORY = 'interactive-map-add-reference-point';
const PLACE_SELECTED_DIFFERENT_CITY_EVENT_CATEGORY =
  'interactive-map-search-different-city';
const HOTJAR_POTENTIAL_PERMANENT_BOOKING = 'potential_permanent_booking';

const VIEW_MAP_LIST_EVENT_CATEGORY = 'Search';
const VIEW_MAP_LIST_EVENT_ACTION = 'View map-list toggle';
const VIEW_MAP_LIST_EVENT_VIEW_MAP_TAG = 'View map';
const VIEW_MAP_LIST_EVENT_VIEW_LIST_TAG = 'View list';

const isDesktopDevice = deviceType => deviceType === 'desktop';

const pushDataLayerProductList = (markersPage, moveIn, moveOut) => {
  const dataLayer = window.dataLayer;
  if (typeof dataLayer !== 'undefined') {
    const productIdList = markersPage.slice(0, 3).map(ru => ru.id);

    dataLayer.push({
      PageType: 'Listingpage',
      HashedEmail: '',
      ProductIDList: productIdList,
      din: moveIn,
      dout: moveOut
    });
  }
};

const NUMBER_OF_HOMECARDS_PER_PAGE = 60;

const handleSaveSearchMobile = () => {
  SoyuzAnalytics.sendGA4Event('Search', {
    category: 'Alert-primary-CTA',
    value: 'click'
  });
  SoyuzAnalytics.sendGA4Event('Search', {
    category: 'SaveMySearchMobile',
    value: 'click'
  });
};

const SaveSearchButtonWrapper = ({ cityId }) => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const showButton = () => setShow(true);
    const hideButton = () => setShow(false);
    Mediator.subscribe(CHANNELS.HOMECARD_MAP_OPEN, hideButton);
    Mediator.subscribe(CHANNELS.HOMECARD_MAP_CLOSE, showButton);

    return () => {
      Mediator.unsubscribe(CHANNELS.HOMECARD_MAP_OPEN, hideButton);
      Mediator.unsubscribe(CHANNELS.HOMECARD_MAP_CLOSE, showButton);
    };
  }, []);

  return (
    show && (
      <div className="quick-alert-mobile__switcher">
        <div className="quick-alert-mobile__floating">
          <AlertButton
            buttonProps={{
              renderIcon: () => <Bell />,
              size: 'medium'
            }}
            cityId={cityId}
            onClick={handleSaveSearchMobile}
            dataTest="alert-button-mobile"
          />
        </div>
      </div>
    )
  );
};

const getPoiFormatted = (poiState, cityId) => {
  if (poiState?.poiLat && poiState?.poiLng) {
    return {
      lat: poiState.poiLat,
      lng: poiState.poiLng,
      name: poiState.poiName || '',
      placeId: poiState.poiId || '',
      cityId
    };
  }
  return null;
};
function Search({
  locationString = '',
  locationCoordinates,
  city,
  countryCode = '',
  currency = undefined,
  maxPriceInCity,
  initialMarkerList = [],
  numberOfMarkers,
  sortingAlgorithm = '',
  itemsPerPage,
  initialHomecards,
  seoMode = false,
  seoContent = null,
  map = false,
  preloadedHomecards,
  isSafari,
  internalLinking,
  seoSnippet,
  deviceType
}) {
  const {
    filters,
    handleUpdateFilters,
    handleUpdatePage,
    handleUpdateMap,
    handleUpdatePoi,
    poiState,
    resetFilters
  } = useFiltersContext();

  const { markerClickedId: selectedMarkerId } = useHighlightMarkersContext();
  const selectedMarkerIdRef = useRef(selectedMarkerId);

  const mapRef = useRef(null);
  const userHasInteractedWithTheMap = useRef(false);
  const mapBoundariesRef = useRef(null);
  const cdManagerRef = useRef(null);
  const cardPaginatorRef = useRef(
    new SimplifiedCardPaginator({
      initialMarkers: initialMarkerList,
      initialPage: filters.page,
      pageSize: itemsPerPage,
      numberOfMarkers
    })
  );

  const previousFilters = useRef(filters);

  const [paginatedMarkers, setPaginatedMarkers] = useState(initialMarkerList);
  const [numberOfMarkersState, setNumberOfMarkers] = useState(numberOfMarkers);
  const [isMapLoadRequested, setIsMapLoadRequested] = useState(map);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [newMapBoundaries, setNewMapBoundaries] = useState(false);
  const [maxTotalResults, setMaxTotalResults] = useState(0);
  const [isFiltersHidden, setIsFiltersHidden] = useState(true);
  const deferredIsFiltersHidden = useDeferredValue(isFiltersHidden);
  const { defaultZoom } = useSoyuzClientConfig();
  const { current: currentLocale } = useSoyuzLocales();

  // Not needed anymore, as we are alwasy asking for the list from the server
  // const rearrangeMarkerList = useMarkerReorder();

  const updateUrlOnFilterChangeIsOn = useSoyuzFlag(
    'UpdateUrlOnFilterChange'
  ).isOn();

  const reduceTheMapPresence = useSoyuzExperiment('ReduceTheMapPresence');

  const shouldUpdateMapCenter = !(
    cityToggleConfig(city.id).shouldNotUpdateUrlOnFiltersChange &&
    updateUrlOnFilterChangeIsOn
  );

  const isRenderMapViewNoSSR = useSoyuzFlag('RenderMapViewNoSSR').isOn();

  const showSeoCharts = shouldShowCharts(numberOfMarkers, filters);

  const {
    isDesktopScreen,
    isMobileScreen,
    isPhabletScreen,
    isTabletScreen,
    isBiggerThanTablet,
    device
  } = useMediaQuery(deviceType);

  const isMobileOrTablet = () =>
    isMobileScreen() || isPhabletScreen() || isTabletScreen();

  const isReduceMapPresenceExperiment =
    !isMobileOrTablet() && reduceTheMapPresence.get('behaviour') === 'variant';

  useEffect(() => {
    selectedMarkerIdRef.current = selectedMarkerId;
  }, [selectedMarkerId]);

  const getAdaptedFilters = useCallback(
    filtersToAdapt =>
      FilterUtils.adaptFiltersFromUrl(filtersToAdapt, sortingAlgorithm),
    [sortingAlgorithm]
  );

  const shouldUpdateMarkersCheck = prevFilters => {
    const currentFilters = filters;
    const filterKeys = Object.keys(currentFilters).filter(
      key => !['map'].includes(key)
    );

    return filterKeys.some(key => currentFilters[key] !== prevFilters[key]);
  };

  const sendViewMapListToggleEvent = isVisible => {
    const label = isVisible
      ? VIEW_MAP_LIST_EVENT_VIEW_MAP_TAG
      : VIEW_MAP_LIST_EVENT_VIEW_LIST_TAG;
    SoyuzAnalytics.sendGA4Event(VIEW_MAP_LIST_EVENT_CATEGORY, {
      category: VIEW_MAP_LIST_EVENT_ACTION,
      value: label
    });
  };

  const handleFiltersUpdate = filtersToBeMerged => {
    const updatedFilters = { ...filters, ...filtersToBeMerged };

    const newFilters = removeSeoFilters(updatedFilters);

    // remove rooms when user deselect apartments
    if (
      filtersToBeMerged['type[]'] &&
      !filtersToBeMerged['type[]'].includes('apartments')
    ) {
      newFilters['topology[]'] = [];
    }

    const filtersChanged = Object.keys(filtersToBeMerged).some(
      key => filtersToBeMerged[key] !== filters[key]
    );

    if (!filtersChanged) {
      return;
    }

    if (filters.page !== 1) {
      cardPaginatorRef.current.setCurrentPage(1);
      handleUpdatePage(1);
    }

    if (updatedFilters.sortBy !== 'POI') {
      const { poiLat, poiLng, poiName, poiId, ...filtersWithoutPOI } =
        newFilters;
      handleUpdateFilters(filtersWithoutPOI);
      return;
    }

    handleUpdateFilters(newFilters);
  };

  const triggerHotJarEventForLongStays = filtersToCheck => {
    const {
      'move-in': moveIn,
      'move-out': moveOut,
      'rentalType[]': rentalType
    } = filtersToCheck;

    const currentCityIsInSpain = city.country.countryIsoCode === 'ES';
    const hasLongTermRentalType = rentalType && rentalType.includes('longTerm');
    const hasLongStayInDates =
      moveIn && moveOut && differenceInMonths(moveOut, moveIn) >= 9;

    if (currentCityIsInSpain && (hasLongTermRentalType || hasLongStayInDates)) {
      Metrics.hotjar.triggerEvent(
        `${HOTJAR_POTENTIAL_PERMANENT_BOOKING}_${currentLocale}`
      );
    }
  };

  const showMap = () => {
    const isServer = typeof window === 'undefined';
    const isManuallyEnabled = filters.map;
    const isDesktop = isServer
      ? isDesktopDevice(deviceType)
      : isBiggerThanTablet();

    setIsMapVisible(
      isManuallyEnabled || (isDesktop && !isReduceMapPresenceExperiment)
    );
  };

  const getMarkers = async () => {
    let updatedTotalMarkers = numberOfMarkersState;
    try {
      const { data } = await getMarkersBoundsQuery(getGqlClient())(
        city.id,
        getAdaptedFilters(filters),
        mapBoundariesRef.current,
        itemsPerPage
      );

      const newMarkers = data.search.markers;
      updatedTotalMarkers = data.search.totalMarkers;

      cardPaginatorRef.current.updateData({
        markers: newMarkers,
        nTotalMarkers: updatedTotalMarkers
      });

      setMaxTotalResults(prevMaxTotalResults =>
        Math.max(prevMaxTotalResults, updatedTotalMarkers)
      );
      setNumberOfMarkers(updatedTotalMarkers);
      setPaginatedMarkers(newMarkers);
      setNewMapBoundaries(false);
    } catch (error) {
      if (error.message !== 'CANCELED') {
        Metrics.hotjar.recordTag('ErrorGetMarkersSearch');
        clientErrorTracker.error({ error, source: 'getMarkersSearch-Error' });
      }
    }

    cdManagerRef.current?.setSearchQueryCustomDimensions(
      city.id,
      getAdaptedFilters(filters),
      updatedTotalMarkers
    );
  };

  const isPrevButtonDisabled = () =>
    cardPaginatorRef.current?.getPreviousPageNumber() === null;
  const isNextButtonDisabled = () =>
    cardPaginatorRef.current?.getNextPageNumber() === null;

  const shouldRenderSeoContent = () =>
    seoMode && !!seoContent && !!seoContent.body;

  useEffect(() => {
    showMap();

    cdManagerRef.current = CustomDimensionsManager();

    if (paginatedMarkers.length > 0) {
      pushDataLayerProductList(
        paginatedMarkers,
        filters['move-in'],
        filters['move-out']
      );
    }
  }, []);

  useEffect(() => {
    const shouldUpdateMarkers = shouldUpdateMarkersCheck(
      previousFilters.current
    );
    previousFilters.current = filters;

    if (shouldUpdateMarkers) {
      getMarkers();
    }

    triggerHotJarEventForLongStays(filters);
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    showMap();
  }, [filters.map, device]);

  useEffect(() => {
    setIsMapLoadRequested(map);
  }, [map]);

  const handlePlaceSelected = async place => {
    if (place) {
      const { name, lat, lng, cityId: newPoiCityId } = place;

      if (name && lat && lng) {
        SoyuzAnalytics.sendGA4Event(PLACE_SELECTED_EVENT_CATEGORY, {
          place: name,
          coord: `${lat},${lng}`
        });
      }

      if (newPoiCityId && city.id !== newPoiCityId) {
        SoyuzAnalytics.sendGA4Event(
          PLACE_SELECTED_DIFFERENT_CITY_EVENT_CATEGORY,
          { currentCity: city.id, newPoiCityId }
        );
      }
    }

    handleUpdatePoi(place);
  };

  const handlePageClick = page => {
    // Keeping track of the current page, not the data
    cardPaginatorRef.current.setCurrentPage(page);
    handleUpdatePage(Number(page));
  };

  const handleNextButtonClick = () => {
    const nextPage = cardPaginatorRef.current?.getNextPageNumber();
    if (nextPage) {
      cardPaginatorRef.current.setCurrentPage(nextPage);
      handleUpdatePage(nextPage);
    }
  };

  const handlePreviousButtonClick = () => {
    const previousPage = cardPaginatorRef.current?.getPreviousPageNumber();
    if (previousPage) {
      cardPaginatorRef.current.setCurrentPage(previousPage);
      handleUpdatePage(previousPage);
    }
  };

  const handleMapWillUnmount = () => {
    // Request first markers page again, with no map bounds
    // cardPaginatorRef.current.restoreOriginalData();
    // onNewPageContent();
    userHasInteractedWithTheMap.current = false;
    mapBoundariesRef.current = {};
  };

  const handleMapDidMount = newMapRef => {
    mapRef.current = newMapRef;
    mapRef.current.resetCenter = () => {
      newMapRef.setZoom(defaultZoom[city.id]);
      newMapRef.setCenter(locationCoordinates);
    };
    mapBoundariesRef.current = mapRef.current.getMapBounds();
  };

  const handleToggleActiveView = () => {
    const toggled = !filters.map;
    handleUpdateMap(
      toggled || (!isMobileOrTablet() && !isReduceMapPresenceExperiment)
    );
    sendViewMapListToggleEvent(toggled);
    setIsMapLoadRequested(toggled || !isMobileOrTablet());
  };

  const handleSwitcherClick = () => {
    handleToggleActiveView();
    SoyuzAnalytics.sendGA4Event('Search', {
      action: 'MapListSwitch',
      value: 'click'
    });
  };

  const handleOnFiltersToggleClick = useCallback(
    e => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      // Use startTransition to mark the toggle as non-urgent
      // This allows React to prioritize the immediate visual response
      startTransition(() => {
        setIsFiltersHidden(!isFiltersHidden);
      });
    },
    [isFiltersHidden]
  );

  const handleSortByChange = async sortBy => {
    if (filters.sortBy === 'POI') {
      await handlePlaceSelected(null);
    }
    handleFiltersUpdate({ sortBy });
  };

  const handleRefreshMapBoundaries = (shouldRestorePagination = false) => {
    setNewMapBoundaries(false);

    if (selectedMarkerIdRef.current) {
      return;
    }

    // Reset paginator to the first page
    if (
      shouldRestorePagination &&
      cardPaginatorRef.current.getCurrentPageNumber() !== 1
    ) {
      cardPaginatorRef.current.setCurrentPage(1);
    }
    getMarkers();
  };

  const handleMapNewBounds = async mapBounds => {
    mapBoundariesRef.current = mapBounds;

    if (selectedMarkerIdRef.current) {
      return;
    }
    const isFirstInteraction = !userHasInteractedWithTheMap.current;

    // When the map is mounted, an idle event is triggered
    if (isMobileOrTablet() || isFirstInteraction) {
      userHasInteractedWithTheMap.current = true;
      setNewMapBoundaries(!isFirstInteraction);
      return;
    }

    if (filters.page !== 1) {
      cardPaginatorRef.current.setCurrentPage(1);
      handleUpdatePage(1);
    } else {
      getMarkers();
    }
  };

  const handleResetMapBoundaries = () => {
    SoyuzAnalytics.sendGA4Event('Search', {
      action: 'ResetMapBoundaries',
      value: 'click'
    });
    AdUrlService.deleteQueryParameter('mapCenter');
    mapRef?.current.resetCenter({ resetZoom: true });
  };

  const handleMapZoomChange = bounds => {
    const { northEast, southWest } = bounds;
    const totalMarkersShown = cardPaginatorRef.current.getLength();

    // This is probably broken... Let's see if anyone complaints
    cdManagerRef.current?.setSearchMapCustomDimensions(
      `${northEast.lat}_${northEast.lng}`,
      `${southWest.lat}_${southWest.lng}`,
      totalMarkersShown
    );

    const label = `${northEast.lat},${northEast.lng},${southWest.lat},${southWest.lng};${totalMarkersShown}`;

    SoyuzAnalytics.sendGA4Event('send', {
      action: 'zoom-map',
      label
    });
  };

  const handleClearFilters = () => {
    if (isDesktopScreen() && mapRef.current !== undefined) {
      mapRef.current?.resetCenter({ resetZoom: true });
    }

    if (filters.page !== 1) {
      cardPaginatorRef.current.setCurrentPage(1);
      handleUpdatePage(1);
    }
    resetFilters();
  };

  const handleCloseFilters = useCallback(
    e => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      if (!isFiltersHidden) {
        // Use startTransition to mark the close as non-urgent
        // This allows React to prioritize the immediate visual response
        startTransition(() => {
          setIsFiltersHidden(true);
        });
        SoyuzAnalytics.sendGA4Event('Search', { action: 'closeFilters' });
      }
    },
    [isFiltersHidden]
  );

  const handleMapLazyLoad = () => {
    setIsMapLoadRequested(true);
    SoyuzAnalytics.sendGA4Event('Search', {
      action: 'MapPlaceholderCTA',
      value: 'click'
    });
  };

  const getSanitizedMarkersList = () => {
    return paginatedMarkers.map(marker => {
      const { totalCountMultilisting, ...rest } = marker;
      return rest;
    });
  };

  const renderMapListSwitcher = isPositionRight => (
    <MapListSwitcher
      onClickLeft={handleSwitcherClick}
      onClickRight={handleSwitcherClick}
      isPositionRight={isPositionRight}
    />
  );

  const renderExposedFilters = ({ showMapListSwitcher = false } = {}) => {
    const totalResults = cardPaginatorRef.current?.getTotalMarkers();

    return (
      <FiltersExposed
        totalResults={totalResults}
        currencyIsoCode={currency?.isoCode}
        onFiltersClose={handleCloseFilters}
        onFiltersToggle={handleOnFiltersToggleClick}
        onFiltersUpdate={handleFiltersUpdate}
        maxPriceInCity={maxPriceInCity}
        minimumStaying={city.minimumStaying}
        cityId={city.id}
        sortingAlgorithm={sortingAlgorithm}
        onClearFilters={handleClearFilters}
        showMapListSwitcher={showMapListSwitcher}
        onSwitcherClick={handleSwitcherClick}
      />
    );
  };

  const renderMapOverlay = () => {
    const isMobile = isMobileOrTablet();
    const showAutocompleter = isMobile || isMapLoadRequested;

    const overlayClasses = classNames('search-map__overlay', {
      'search-map__overlay--variant': isReduceMapPresenceExperiment
    });

    return (
      showAutocompleter && (
        <div className={overlayClasses} data-test="map-overlay">
          <div className={`${isMobile ? 'search-map__filters' : ''}`}>
            <div
              className={classNames('search-map__places--extra-content', {
                'search-map__places': isMobile,
                'search-map__places--reduced': isReduceMapPresenceExperiment
              })}
            >
              <Suspense fallback={null}>
                <PlacesAutocomplete
                  flat={isMobile}
                  boundCoords={locationCoordinates}
                  pointOfInterest={getPoiFormatted(poiState, city.id)}
                  onPlaceSelected={handlePlaceSelected}
                  countryCode={countryCode}
                  cityName={city.cityName}
                  renderExtraContent={() =>
                    isMobile ? renderMapListSwitcher(true) : null
                  }
                  rounded={isReduceMapPresenceExperiment}
                />
              </Suspense>
            </div>
            {isMobile && renderExposedFilters()}
          </div>

          {isMobile && (
            <FabButton
              className={classNames('search-map__refresh-button-rebranding', {
                'search-map__refresh-button-rebranding--hidden':
                  !newMapBoundaries
              })}
              color="secondary-alt"
              onClick={handleRefreshMapBoundaries}
              dataTest="searchHereButton"
              renderContent={() => trans('search.search-here')}
              renderIcon={() => <RefreshIcon />}
            />
          )}

          <HomecardMap
            cityId={city.id}
            deviceType={deviceType}
            context={
              isReduceMapPresenceExperiment ? 'search-without-map' : 'search'
            }
          />
        </div>
      )
    );
  };

  const renderMap = () => {
    const shouldShowMap = isMapLoadRequested;

    if (!locationCoordinates) {
      return null;
    }

    return shouldShowMap ? (
      <Suspense fallback={null}>
        <Map
          mapId={isReduceMapPresenceExperiment ? MAP_ID_VARIANT : MAP_ID}
          currencyIsoCode={currency?.isoCode}
          centerCoordinates={locationCoordinates}
          defaultZoom={defaultZoom[city.id]}
          markerList={getSanitizedMarkersList()}
          pointOfInterest={getPoiFormatted(poiState, city.id)}
          onMarkerClicked={() => setIsFiltersHidden(true)}
          onMapDidMount={handleMapDidMount}
          onMapWillUnmount={handleMapWillUnmount}
          onIdle={handleMapNewBounds}
          onZoomChange={handleMapZoomChange}
          showPrice
          isMapLoadDelayed
          shouldUpdateMapCenter={shouldUpdateMapCenter}
          isReduceMapPresenceExperiment={isReduceMapPresenceExperiment}
        />
      </Suspense>
    ) : (
      <MapPlaceholder
        city={city.id}
        cityCenterCoords={locationCoordinates}
        onButtonClick={() => {
          handleMapLazyLoad();
        }}
      />
    );
  };

  const mapView = () => (
    <section
      className={classNames('search-map', {
        'search-map--original': !isReduceMapPresenceExperiment,
        'search-map--variant': isReduceMapPresenceExperiment
      })}
    >
      {isReduceMapPresenceExperiment && (
        <div className="search-listing__total-results-map">
          <SearchTitle
            showButtons={false}
            cityName={city.cityName}
            totalResults={numberOfMarkersState}
            onSortChange={handleSortByChange}
            onClearFilters={handleClearFilters}
          />
        </div>
      )}
      <NoSSR>{renderMapOverlay()}</NoSSR>
      {renderMap()}
    </section>
  );

  const renderMapView = () => {
    if (!isMapVisible) {
      return null;
    }

    return isRenderMapViewNoSSR ? <NoSSR>{mapView()}</NoSSR> : mapView();
  };

  /**
   * TO-DO
   * Pagination is BROKEN
   * When moving around the map, and filtering to a few hundred of properties,
   * the paginator renders fully, with the whole collection paginated, up to 271 pages in Madrid.
   * If you go to the last pages, they are all empty.
   *
   * The paginator should get the amount of pages with content
   */
  const renderPaginator = () => {
    const nextIsDisabled = isNextButtonDisabled();
    const previousIsDisabled = isPrevButtonDisabled();

    return (
      cardPaginatorRef.current?.getTotalPages() > 1 && (
        <Paginator
          currentPage={cardPaginatorRef.current?.getCurrentPageNumber()}
          totalPages={cardPaginatorRef.current?.getTotalPages()}
          previousButtonDisabled={previousIsDisabled}
          nextButtonDisabled={nextIsDisabled}
          onPreviousButtonClick={handlePreviousButtonClick}
          onNextButtonClick={handleNextButtonClick}
          onPageClick={handlePageClick}
          prevPage={cardPaginatorRef.current?.getPreviousPageNumber()}
          nextPage={cardPaginatorRef.current?.getNextPageNumber()}
          locationString={locationString}
        />
      )
    );
  };

  const renderResetMap = noResults => {
    const classes = classNames('search-listing__reset-map', {
      'search-listing__reset-map--big': noResults
    });
    return (
      <div className={classes}>
        <Button
          color="secondary"
          onClick={handleResetMapBoundaries}
          size="large"
        >
          {trans('search.reset-map')}
        </Button>
      </div>
    );
  };

  const renderHomecardList = () => {
    const hasZeroResults = cardPaginatorRef.current?.getTotalMarkers() === 0;
    const markerList = paginatedMarkers;

    if (markerList.length === 0) {
      SoyuzAnalytics.sendGA4Event('Search', {
        action: 'no-results'
      });
    }

    if (markerList.length < NUMBER_OF_HOMECARDS_PER_PAGE) {
      SoyuzAnalytics.sendGA4Event('Search', {
        action: 'incomplete-page',
        value: markerList.length
      });
    }

    const numberOfSoldoutHomecards = markerList.filter(hc => hc.so).length;

    if (numberOfSoldoutHomecards > 0) {
      SoyuzAnalytics.sendGA4Event('Search', {
        section: 'soldout-homecards',
        value: numberOfSoldoutHomecards,
        number_of_items: markerList.length
      });
    }

    const noMorePagesInMap =
      isMapLoadRequested &&
      userHasInteractedWithTheMap.current &&
      cardPaginatorRef.current?.getCurrentPageNumber() === 1 &&
      cardPaginatorRef.current?.hasHiddenResults();
    const currentPageResults = paginatedMarkers.length;
    const noResultsOnTheCurrentPage = currentPageResults === 0;

    return (
      <>
        <div className="search-listing">
          <div className="search-listing__total-results">
            <SearchBanner cityId={city.id} />
            <SearchTitle
              showButtons
              cityName={city.cityName}
              totalResults={numberOfMarkersState}
              currentPageResults={noMorePagesInMap ? currentPageResults : null}
              onSortChange={handleSortByChange}
              onClearFilters={handleClearFilters}
            />
          </div>
          {hasZeroResults ? (
            <RemoveFilters onFiltersUpdate={handleFiltersUpdate} />
          ) : null}
          <HomeCardList
            initialHomecards={initialHomecards}
            markerList={paginatedMarkers}
            cityId={city.id}
            showAlertCreationReminder={isNextButtonDisabled()}
            preloadedHomecards={preloadedHomecards}
            hasLazyLoad
            deviceType={deviceType}
            isReduceMapPresenceExperiment={isReduceMapPresenceExperiment}
          />
          {noMorePagesInMap
            ? renderResetMap(noResultsOnTheCurrentPage)
            : renderPaginator()}
        </div>
        <>
          {showSeoCharts ? (
            <ChartsSection
              location={{
                city: city.cityName,
                country: city.country.countryId
              }}
              isMobile={isMobileOrTablet()}
            />
          ) : null}

          <InViewEvent
            section="search-seo-content"
            className="search-seo-content"
          >
            <SearchBreadcrumbs
              cityId={city.id}
              cityName={city.cityName}
              countryId={city.country.countryId}
              propertyType={filters['type[]']}
              topology={filters['topology[]']}
            />

            <InternalLinkingModules internalLinking={internalLinking} />

            {shouldRenderSeoContent() && (
              <SEOContent
                title={seoContent.title}
                subtitle={seoContent.subtitle}
                body={seoContent.body}
              />
            )}

            <SEOSnippet seoSnippet={seoSnippet} />
          </InViewEvent>
        </>
      </>
    );
  };

  const renderFullFilters = () => {
    // Show the container immediately for visual feedback
    const shouldShowContainer = !isFiltersHidden;
    // Use the deferred value for the heavy content
    const shouldShowContent = !deferredIsFiltersHidden;

    if (!shouldShowContainer) {
      return null;
    }

    return (
      <NoSSR>
        <div className="search__filters-full">
          {shouldShowContent && (
            <Suspense fallback={<div className="filters__loading" />}>
              <Filters
                currency={currency}
                maxPriceInCity={maxPriceInCity}
                onFiltersUpdate={handleFiltersUpdate}
                cityId={city.id}
                onClearFilters={handleClearFilters}
                onCloseFilters={handleCloseFilters}
                totalResults={cardPaginatorRef.current?.getTotalMarkers()}
              />
            </Suspense>
          )}
        </div>
        <FiltersHeader
          results={numberOfMarkersState}
          citySlug={city.id}
          totalResults={cardPaginatorRef.current?.getTotalMarkers()}
          onHandleFiltersToggle={handleOnFiltersToggleClick}
          maxTotalResults={maxTotalResults}
          currency={currency}
          isFooterInSearchPage
        />
      </NoSSR>
    );
  };

  const isDesktop =
    typeof window === 'undefined'
      ? isDesktopDevice(deviceType)
      : isBiggerThanTablet();
  const isMobileOrTabletOnMapView = filters.map && !isDesktop;

  const renderMapListSwitcherWrapper = () =>
    renderMapListSwitcher(isMobileOrTabletOnMapView);

  const headerContainerClass = classNames('wrapper-header', {
    'wrapper-header--hidden': isMobileOrTabletOnMapView
  });

  return (
    <>
      {!isMobileOrTabletOnMapView && (
        <div className={headerContainerClass}>
          <HeaderContainer renderExtraContent={renderMapListSwitcherWrapper} />
          <NoSSR>
            {renderExposedFilters({
              showMapListSwitcher: isReduceMapPresenceExperiment
            })}
          </NoSSR>
        </div>
      )}

      <div id="wrapper-search" data-browser={isSafari ? 'safari' : 'other'}>
        <div className="layout-search">
          <div className="search-results">
            <div
              className={classNames('search__sidebar', {
                'search__sidebar--original': !isReduceMapPresenceExperiment,
                'search__sidebar--hidden':
                  isReduceMapPresenceExperiment && isMapVisible
              })}
            >
              {renderFullFilters()}
              <div id="search-scroll" className="search__scroll">
                {!isReduceMapPresenceExperiment && (
                  <ValueProposition className="search-info-banner" />
                )}
                {renderHomecardList()}
              </div>
            </div>

            <SaveSearchButtonWrapper cityId={city.id} />

            {renderMapView()}
            {isMobileOrTabletOnMapView && renderFullFilters()}
          </div>
          <FooterSimplified />
        </div>
      </div>

      <NoSSR>
        <AppliedFiltersModal cityId={city.id} />
        <AlertInPastModal />
      </NoSSR>
    </>
  );
}

Search.propTypes = {
  locationString: PropTypes.string,
  locationCoordinates: PropTypes.shape({
    lat: PropTypes.string,
    lng: PropTypes.string
  }).isRequired,
  city: PropTypes.shape({
    id: PropTypes.string.isRequired,
    cityName: PropTypes.string.isRequired,
    minimumStaying: PropTypes.number.isRequired,
    center: PropTypes.shape(mapPointShape),
    country: PropTypes.shape({
      countryId: PropTypes.string,
      countryIsoCode: PropTypes.string.isRequired
    })
  }).isRequired,
  countryCode: PropTypes.string,
  currency: PropTypes.shape({ isoCode: PropTypes.string }),
  maxPriceInCity: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    map: PropTypes.bool,
    'type[]': PropTypes.arrayOf(PropTypes.string),
    'topology[]': PropTypes.arrayOf(PropTypes.string),
    page: PropTypes.number
  }),
  initialMarkerList: PropTypes.arrayOf(PropTypes.object),
  numberOfMarkers: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string,
  itemsPerPage: PropTypes.number.isRequired,
  initialHomecards: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      isInstantBooking: PropTypes.bool
    })
  }).isRequired,
  seoMode: PropTypes.bool,
  seoContent: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    body: PropTypes.string
  }),
  map: PropTypes.bool,
  preloadedHomecards: PropTypes.arrayOf(PropTypes.string).isRequired,
  isSafari: PropTypes.bool.isRequired,
  internalLinking: PropTypes.arrayOf(PropTypes.shape(internalLinkingModule))
    .isRequired,
  seoSnippet: PropTypes.shape(SeoSnippetShape).isRequired,
  deviceType: PropTypes.string.isRequired
};

export default Search;
